const MIN_INTERVAL = 1;

/** True unless the browser tab is hidden (another tab in front, minimized window, ...). */
export const isDocumentVisible = () => document.visibilityState !== 'hidden';

export type PollingCallback = () => unknown;

/**
 * Repeats a callback at a fixed interval, but only while the browser tab is visible.
 *
 * Nobody looks at a hidden tab, so its refreshes would only load the server (and, for a super-admin,
 * count as activity in the company timesheet — see ActivityMiddleware in rp-ms-core). The timer is
 * dropped when the tab gets hidden and armed again when it is shown; if a tick was due meanwhile the
 * callback runs right away, so the view catches up the moment the user returns.
 *
 * `start()`/`stop()` express what the owner wants (e.g. an auto-update toggle) and must be paired
 * with the owner's connect/disconnect, otherwise a detached component keeps polling for good.
 */
export class Polling {

	private _timer: number | null = null;
	private _running = false;
	private _interval: number;
	private _lastRun = 0;

	constructor(private readonly callback: PollingCallback, interval: number) {
		this.interval = interval;
	}

	/** Whether the owner asked for polling (a hidden tab keeps this true while no timer is armed). */
	get isRunning() { return this._running }

	get interval() { return this._interval }

	set interval(value: number) {
		if (!(value >= MIN_INTERVAL)) throw new Error(`Interval must be at least ${MIN_INTERVAL} ms`);
		this._interval = value;
		if (this._timer != null) {
			this._clear();
			this._arm();
		}
	}

	start() {
		if (this._running) return;
		this._running = true;
		this._lastRun = Date.now();
		document.addEventListener('visibilitychange', this._onVisibilityChange);
		this._arm();
	}

	stop() {
		if (!this._running) return;
		this._running = false;
		document.removeEventListener('visibilitychange', this._onVisibilityChange);
		this._clear();
	}

	private _arm() {
		if (!isDocumentVisible()) return; // armed by _onVisibilityChange once the tab is shown
		this._timer = window.setInterval(() => this._tick(), this._interval);
	}

	private _clear() {
		if (this._timer == null) return;
		window.clearInterval(this._timer);
		this._timer = null;
	}

	private _tick() {
		this._lastRun = Date.now();
		try {
			const result = this.callback();
			if (result instanceof Promise) result.catch(e => console.error('Polling failed', e));
		} catch (e) {
			console.error('Polling failed', e); // a failing tick must not kill the loop
		}
	}

	private _onVisibilityChange = () => {
		if (!isDocumentVisible()) {
			this._clear();
		} else if (this._timer == null) {
			this._arm();
			if (Date.now() - this._lastRun >= this._interval) this._tick(); // a tick was due while hidden
		}
	}

}
